<template>
  <div class="calendar">
    <div class="calendar__days-selector">
      <div class="calendar-main" :class="{'calendar-main__tight': selectionMode == 'month'}">
        <TypeSelector class="calendar__type-selector" :value="selectionMode" :fallbackVersion="fallbackVersion" @change="setSelectedMode"/>
        <div class="calendar__selector-info" v-if="fallbackVersion && selectionMode == 'date'">
          Wybierz najwcześniejszą datę wylotu oraz przedział z liczbą dni pobytu
        </div>

        <template v-if="selectionMode != 'month'">
          <MonthNavigator class="calendar__month-navigator" :value="display" :valid="valid" @previous="selectPreviousViewMonth" @next="selectNextViewMonth" />
          <div class="calendar__months">
            <div class="calendar__month calendar__month--left">
              <WeekDays class="calendar__week-days" />
              <Month :date="display.start" :valid="valid" :selected="selected" :highlighted="highlighted"
                @change="selectDay" @hover="hoverDay" @clearHover="clearHover"
              />
            </div>
            <div class="calendar__month calendar__month--right">
              <WeekDays class="calendar__week-days" />
              <Month :date="display.end" :valid="valid" :selected="selected" :highlighted="highlighted"
                @change="selectDay" @hover="hoverDay" @clearHover="clearHover"
              />
            </div>
          </div>
        </template>

        <MonthSelector v-if="selectionMode == 'month'" class="calendar__month-selector"
          :validDates="validDates" :selected="selected"
          @click="selectMonth" @hover="hoverMonth" @clearHover="clearHover" />

        <Debugger v-if="showDebugger"
          :selectionMode="selectionMode" :valid="valid" :highlighted="highlighted" :selected="selected" :display="display" />
      </div>
    </div>
    <Range class="calendar__range-selector"
      v-if="selectionMode == 'date'"
      :value="range"
      :ranges="ranges" :enabled="validRanges"
      @input="rangeSelected" />
  </div>
</template>

<script>
import moment from 'moment'
import Vue from 'vue'
import Component from 'vue-class-component'

import Debugger from './calendar/Debugger.vue'
import Month from './calendar/Month.vue'
import MonthNavigator from './calendar/MonthNavigator.vue'
import MonthSelector from './calendar/MonthSelector.vue'
import Range from './calendar/Range.vue'
import TypeSelector from './calendar/TypeSelector.vue'
import WeekDays from './calendar/WeekDays.vue'

window.moment = moment

@Component({
  props: {
    mode: {
      required: false,
      type: String, // 'start', 'end', 'date', 'month'
      default: () => 'start',
    },
    selectedDates: {
      required: true,
      type: Object, // Moment.range
    },
    validDates: {
      required: true,
      type: Object, // Moment.range
    },
    clearedDate: {
      required: true,
      type: Object, // Moment.range      
    },
    range: {
      required: false,
      type: String, // Example: '1:5'
      default: () => '',
    },
    ranges: {
      require: false,
      type: Array,
      default: () => [ ], // Example: [ '1:5', '6:9' ]
    },
    validRanges: {
      require: false,
      type: Array,
      default: () => [],
    },
    fallbackVersion: {
      required: false,
      type: Boolean,
    },
  },
  watch: {
    clearedDate () {
      this.selectionMode = 'start'
      this.selected = moment.range(this.validDates.start, this.validDates.end)
      this.display = this._getDisplayRangeForCurrentSelectionMode()
      this.clearHover()
    },
  },
  components: {
    Debugger,
    TypeSelector,
    MonthNavigator,
    WeekDays,
    Month,
    MonthSelector,
    Range,
  },
})
export default class Calendar extends Vue {
  showDebugger = false

  selectionMode = this.mode
  selected = moment.range(this.selectedDates.start, this.selectedDates.end)
  display = this._getDisplayRangeForCurrentSelectionMode()
  valid = moment.range(this.validDates.start, this.validDates.end)
  highlighted = moment.range(0, 0)

  created () {
    log.debug('Calendar.created',
      'mode:', this.mode,
      'selectionMode', this.selectionMode,
      'selected', this.selected.start.format('YYYY-MM-DD'), this.selected.end.format('YYYY-MM-DD'),
      'validDates', this.validDates.start.format('YYYY-MM-DD'), this.validDates.end.format('YYYY-MM-DD'),
      'valid', this.valid.start.format('YYYY-MM-DD'), this.valid.end.format('YYYY-MM-DD'),
      'display', this.display.start.format('YYYY-MM-DD'), this.display.end.format('YYYY-MM-DD'),
      'range', this.range,
    )

    if (this.selectionMode == 'date') {
      this.selected = moment.range(this.selected.start, 0)
    }
  }

  selectPreviousViewMonth () {
    log.debug('Calendar.selectPreviousViewMonth')
    if (this.valid.start.isBefore(moment(this.display.start).startOf('month'))) {
      this.display = moment.range(this.display.start.subtract(1, 'month'), this.display.end.subtract(1, 'month'))
    }
  }

  selectNextViewMonth () {
    log.debug('Calendar.selectNextViewMonth')
    if (this.valid.end.isAfter(moment(this.display.end).endOf('month'))) {
      this.display = moment.range(this.display.start.add(1, 'month'), this.display.end.add(1, 'month'))
    }
  }

  selectStart (day) {
    this.selected = moment.range(day, day.isAfter(this.selected.end) ? 0 : moment(this.selected.end))
    this.clearHover()
    this.$emit('selectFrom', moment(this.selected.start))
  }

  selectEnd (day, { isAutomaticShift = false } = {}) {
    this.selected = moment.range(this.selected.start, day)
    this.clearHover()
    this.$emit('selectTo', moment(this.selected.end), { isAutomaticShift })
  }

  selectDate (day) {
    this.selected = moment.range(day, 0)
    this.$emit('selectDate', moment(this.selected.start))
  }

  selectDay (day) {
    if (this.selectionMode === 'start') {
      this._toggleSelectionModeInRangeMode()
      this.selectStart(day)
      if (day.isSame(this.selected.end, 'day')) {
        this.selectEnd(moment(day).add(1, 'day'), { isAutomaticShift: true })
      }
    } else if (this.selectionMode === 'end') {
      if (day.isBefore(this.selected.start) || day.isSame(this.selected.start, 'day')) {
        this.selectStart(day)
      } else {
        this._toggleSelectionModeInRangeMode()
        this.selectEnd(day)
      }
    } else if (this.selectionMode === 'date') {
      this.selectDate(day)
    }
  }

  selectMonth (month) {
    const start = moment(month).startOf('month')
    const end = moment(month).endOf('month')
    
    if (this.selectionMode === 'month') {
      this.selected = moment.range(start, end)
      this.$emit('selectMonth', start, end)
    }
  }

  setSelectedMode (mode) {
    if (mode === 'range') {
      this.setRangeMode()
    } else if (mode === 'date') {
      this.setDayAndRangeMode()
    } else if (mode === 'month') {
      this.setMonthMode()
    }
    this.$emit('selectionModeChanged', this.selectionMode, this.selected.start, this.selected.end, this.range)
  }

  updateDisplayForRangeMode () {
    this.display = this._getDisplayRangeForCurrentSelectionMode()
  }

  setRangeMode () {
    this.selectionMode = this.selected.start == 0 ? 'start' : 'end'
    this.selected = moment.range(this.selected.start, this.selected.end)
    this.display = this._getDisplayRangeForCurrentSelectionMode()
    this.clearHover()
  }

  setMonthMode () {
    this.mode = 'month'
    this.selectionMode = 'month'
    this.selected = moment.range(this.selected.start, this.selected.end)
    this.display = this._getDisplayRangeForCurrentSelectionMode()
    this.clearHover()
  }

  _getDisplayRangeForStartMode () {
    log.debug('_getDisplayRangeForStartMode', 1)
    return moment.range(
      moment(this.selected.start).startOf('month'),
      moment(this.selected.start).add(1, 'month').endOf('month'))
  }

  _getDisplayRangeForEndMode () {
    if (!this.selected.end.isSame(moment(0)) && this.selected.end.diff(this.selected.start, 'month') > 1) {
      log.debug('_getDisplayRangeForEndMode', 1)
      return moment.range(
        moment(this.selected.end).subtract(1, 'month').startOf('month'),
        moment(this.selected.end).endOf('month'))
    } else {
      log.debug('_getDisplayRangeForEndMode', 2)
      return moment.range(
        moment(this.selected.start).startOf('month'),
        moment(this.selected.start).add(1, 'month').endOf('month'))
    }
  }

  _getDisplayRangeForSelectionMode (mode) {
    log.debug('_getDisplayRangeForSelectionMode', mode)
    if (mode === 'start' || mode === 'date') {
      return this._getDisplayRangeForStartMode()
    } else {
      return this._getDisplayRangeForEndMode()
    }
  }

  _getDisplayRangeForCurrentSelectionMode () {
    log.debug('_getDisplayRangeForCurrentSelectionMode', this.selectionMode)
    return this._getDisplayRangeForSelectionMode(this.selectionMode)
  }

  setDayAndRangeMode () {
    this.selectionMode = 'date'
    this.selected = moment.range(this.selected.start, 0)
    this.display = this._getDisplayRangeForCurrentSelectionMode()
    this.clearHover()
  }

  _toggleSelectionModeInRangeMode () {
    if (this.selectionMode === 'start') this.selectionMode = 'end'
    else if (this.selectionMode === 'end') this.selectionMode = 'start'
    this.$emit('selectionModeChanged', this.selectionMode, this.selected.start, this.selected.end, this.range)
  }

  rangeSelected (range) {
    this.$emit('selectRange', range)
  }

  hoverDay (day) {
    if (this.selectionMode === 'start') {
      if (day.isBefore(this.selected.end)) this.highlighted = moment.range(day, this.selected.end)
      this.$emit('hoverFrom', moment(day), 'start')
      this.$emit('hoverTo', moment(this.selected.end), 'start')
    } else if (this.selectionMode === 'end') {
      if (day.isBefore(this.selected.start) || day.isSame(this.selected.start, 'day')) {
        this.highlighted = moment.range(day, this.selected.end)
        this.$emit('hoverFrom', moment(day), 'start')
        this.$emit('hoverTo', moment(this.selected.end), 'start')
      } else {
        this.highlighted = moment.range(this.selected.start, day)
        this.$emit('hoverFrom', moment(this.selected.start), 'end')
        this.$emit('hoverTo', moment(day), 'end')
      }
    } else {
      this.$emit('hoverDay', moment(day), 'date')
    }
  }

  hoverMonth (month) {
    const start = moment.utc(month).startOf('month')
    const end = moment.utc(month).endOf('month')
    
    if (this.selectionMode === 'month') {
      this.$emit('hoverMonth', start, end)
    }
  }

  clearHover () {
    this.highlighted = moment.range(0, 0)
    if (this.selectionMode === 'date') {
      this.$emit('hoverDay', moment(this.selected.start), this.selectionMode)
    } else if (this.selectionMode === 'month') {
      this.$emit('hoverMonth', moment(this.selected.start, this.selected.end))
    } else {  
      this.$emit('hoverFrom', moment(this.selected.start), this.selectionMode)
      this.$emit('hoverTo', moment(this.selected.end), this.selectionMode)
    }
  }

  close () {
    this.$emit('close')
  }
}
</script>

<style lang="scss">
@import './consts';
@import './calendar/consts';

.calendar {
  border: none;
  font-family: Gotham;
  display: flex;
  flex: 1 0 auto;
  position: relative;
  border: none;
  margin: 0;

  &-main {
    display: flex;
    flex-direction: column;
    flex: 1 0 auto;
    width: 460px;
  }

  &__type-selector {
    border-bottom: solid 15px white;
    position: relative;
    margin: 0;
  }

  &__selector-info {
    color: #22B6FB;
    font-size: 12px;
    font-weight: 300;
    line-height: 20px;
    text-align: center;
    text-transform: none;
    margin: 0 5px 9px -7px;
  }

  &__month-navigator {
    margin: 10px 0;
  }

  &__months {
    display: flex;
    justify-content: space-between;
    margin: 0 5px 25px 2px;
  }

  &__week-days {
    font-size: 12px;
    margin-bottom: 7px;
  }

  &__range-selector {
    width: 131px;
    padding: 0 0 0 0 !important;
    border-top: 44px solid #EEE;
  }

  & .month__day, & .week-days__day {
    width: 30px;
    height: 30px;
  }
}
</style>

<style lang="scss">
.custom-select-date__container,
.custom-select-date__error {
  position: absolute;
  top: 41px;
  z-index: 1100;
  display: none;
  left: 0;
  background-color: transparent;

  @include media(tablet) {
    left: initial;
    right: 0;
  }
  @include media(desktop) {
    left: 0;
    right: initial;
  }
}
</style>

<!--
  This one is an EXCEPTION!
  Actually we have two differen types of calendar.
  This part is needed to support the old one calendar version (dates selection + days range)
  Its only used on hotel page so thats why I've used '.page--offer' classname.
-->
<style lang="scss">
@import './dialog';
.page--offer .calendar {
  @include dialog();
  padding-left: 1px;

  &-main {
    width: 499px;
  }

  &__type-selector {
    padding: 1px 0 0 2px;
  }

  &__days-selector {
    margin: 0px 0 9px 0px;
  }

  &__month-navigator {
    margin: 10px 0 11px 0;
  }

  &__months {
    flex-direction: row;
    flex: 1 0 auto;
    margin: 0 0 0px 5px;
  }

  &__month {
    flex: 1 0 auto;

    &--left {
      margin: 0 20px 20px 14px;
    }

    &--right {
      margin: 0 25px 20px 14px;
    }
  }

  &__close {
    position: absolute;
    top: 1px;
    right: 2px;
    color: #005ca9;

    button {
      font-size: 19px;
      font-family: "Gotham";
      font-weight: 500;
      background: none;
      border: 0;
    }    
  }

  .type-selector {
    justify-content: space-around;
    background-color: #eeeeee;

    &__item-wrapper {
      flex: 0 1 auto;
      margin: 0 -5px 0 0;
    }

    &__item {
      line-height: 0;
      font-weight: bold;
      padding: 21px 0px 17.5px 0px;
      color: #535353;
      margin: 0;
      border: none;

      &:hover {
        color: #535353;
        text-decoration: none;
      }

      &:first-child,
      &:last-child {
        border-radius: 0;
      }
    }

    &__item--active {
      border-bottom: 4px solid #009FEA;
      color: #009FEA;
      background-color: transparent;

      &:hover {
        color: #009FEA;
      }
    }
  }

  .month-navigator__navigator {
    padding: 4px 2px;
    display: flex;
    align-items: center;
    margin-top: -5px;

    &--left {
      margin: -5px -7px 0 14px;
    }

    &--right {
      margin: -3px 16px 0 -6px;
    }
  }
}
</style>


// WEBPACK FOOTER //
// src/components/Calendar.vue